// Create a new meet_rooms row + auto-add the creator as an owning member.
// camelCase API at the boundary; snake_case at the row write — same boundary
// chat's createChannel uses.
//
// migrated 2026-04-27 to SDK route workspaceProject.meet.rooms (create) +
// workspaceProject.meet.members (create)
import { getSDK } from '@symbo-ls/workspace-shared/functions/sdk.js'
import { rowToRoom } from './meetUtils.js'

export const createRoom = async function ({
  name,
  kind = 'room',
  // `visibility` is the new 3-state model (private/workspace/public).
  // `isPrivate` kept for back-compat — only consulted when visibility is
  // not supplied. Default is 'workspace' (the most common case: a room
  // any teammate can join, but not anyone with the URL).
  visibility,
  isPrivate = false,
  transcribeEnabled = false,
  calendarEventId = null,
  chatChannelId = null
}) {
  const finalVisibility = visibility || (isPrivate ? 'private' : 'workspace')
  const finalIsPrivate = finalVisibility === 'private'
  const root = this.state.root
  const me = root.currentUserId
  if (!me) {
    console.error('[createRoom] no currentUserId — meet requires real auth')
    return null
  }
  const sdk = getSDK()

  try {
    const result = await sdk.execute('workspaceProject.meet.rooms', 'create', {
      payload: {
        name: name || this.call('polyglot', 'meet.room.untitled'),
        kind,
        visibility: finalVisibility,
        // Dual-write is_private until consumers fully migrate to visibility.
        // Drop this once everyone reads `visibility` (see migration 0149 notes).
        is_private: finalIsPrivate,
        transcribe_enabled: transcribeEnabled,
        calendar_event_id: calendarEventId,
        chat_channel_id: chatChannelId,
        created_by: me,
        // Name the workspace the tab is ACTUALLY viewing (URL-derived) — the
        // server membership-validates and stamps the room into it (SERVER-
        // CHAT-WS-SCOPE-EXPLICIT). Without this, rooms stamped the token's
        // possibly-stale active-workspace claim (the exact 'stale
        // workspace_id' case MeetService's visibility docs flag).
        workspace_id: root.workspace?.id ? String(root.workspace.id) : undefined
      }
    })
    // PostgREST returns inserts as an array even when only one row was
    // written. Unwrap to a single row — same shape as the chat surface's
    // create handlers.
    const data = result?.data ?? result
    let row = Array.isArray(data) ? data[0] : data
    // Empty-response fallback for RLS-masked SELECT after INSERT — see
    // tickets/server.md SERVER-CHAT-AUTH-1.
    if (!row?.id) {
      try {
        const refetch = await sdk.execute('workspaceProject.meet.rooms', 'list', {
          filter: { created_by: me, kind },
          options: { order: { created_at: 'desc' }, limit: 5 }
        })
        const rows = refetch?.data || refetch || []
        const list = Array.isArray(rows) ? rows : []
        row = list.find((r) => r && (name == null || r.name === name)) || null
      } catch (refetchErr) {
        console.warn(
          '[createRoom] empty-response refetch failed:',
          refetchErr?.message || refetchErr
        )
      }
    }
    if (!row?.id) throw new Error('createRoom: empty response, refetch found no row')

    const room = rowToRoom(row)

    // Auto-add the creator as the owning member. Other members can self-join
    // via the shareable URL (the meet_members_insert RLS policy allows
    // user_id = auth.uid() inserts).
    //
    // user_id is OMITTED on purpose: `root.currentUserId` is the MONGO _id
    // (dual id-space — see the agents-model memory) while meet_members.user_id
    // is `uuid references auth.users(id)`, so passing `me` failed every
    // insert with `invalid input syntax for type uuid` and rooms ended up
    // with NO owning member row. The server's meet surface stamps user_id
    // from auth.supabaseUserId whenever the body doesn't provide one — the
    // designed self-join path (server surfaces/meet.js).
    try {
      await sdk.execute('workspaceProject.meet.members', 'create', {
        roomId: room.id,
        payload: { role: 'owner' }
      })
    } catch (memErr) {
      console.warn('[createRoom] member insert failed', memErr?.message || memErr)
    }

    // Optimistic state merge — realtime will deliver the same row but the
    // user shouldn't have to wait for the round trip.
    const existing = root.rooms || []
    if (!existing.some((r) => r.id === room.id)) {
      root.update({
        rooms: [room, ...existing],
        activeRoomId: room.id
      })
    }
    // Pop the new-room modal layer if it's open. Safe to call when no
    // layer is open — closeModal early-returns when id isn't on the stack.
    try {
      this?.call?.('closeNewRoomModal')
    } catch (err) {
      console.warn('[createRoom] closeNewRoomModal failed:', err)
    }

    return room.id
  } catch (err) {
    console.error('[createRoom]', err?.message || err)
    return null
  }
}
